// platform/src/components/mw/MwButton.tsx
//
// Botón MW. Variants primary/secondary/ghost/accent.
// Tokens consumidos desde styles/mw.css.

import type { ButtonHTMLAttributes, ReactNode, CSSProperties } from 'react'
import { useState } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'accent'
type Size = 'sm' | 'md' | 'lg'

interface MwButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: Variant
  size?: Size
  asAnchor?: boolean
  href?: string
}

interface VariantStyle {
  bg: string
  bgHover: string
  color: string
  colorHover: string
  border: string
  borderHover: string
}

const VARIANT_STYLES: Record<Variant, VariantStyle> = {
  primary: {
    bg: '#2563EB',
    bgHover: '#1D4ED8',
    color: '#ffffff',
    colorHover: '#ffffff',
    border: '#2563EB',
    borderHover: '#1D4ED8',
  },
  secondary: {
    bg: 'var(--mw-surface)',
    bgHover: 'var(--mw-surface-section)',
    color: 'var(--mw-ink)',
    colorHover: 'var(--mw-ink)',
    border: 'var(--mw-border)',
    borderHover: 'var(--mw-border-strong)',
  },
  ghost: {
    bg: 'transparent',
    bgHover: 'rgba(19, 24, 48, 0.05)',
    color: 'var(--mw-ink)',
    colorHover: 'var(--mw-ink)',
    border: 'transparent',
    borderHover: 'transparent',
  },
  accent: {
    bg: 'var(--mw-accent)',
    bgHover: 'var(--mw-accent-hover)',
    color: 'var(--mw-ink-inverse)',
    colorHover: 'var(--mw-ink-inverse)',
    border: 'transparent',
    borderHover: 'transparent',
  },
}

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm',
  md: 'h-11 px-6 text-[15px]',
  lg: 'h-13 px-8 text-base',
}

const BASE = `
  inline-flex items-center justify-center gap-2
  font-medium tracking-tight
  rounded-full border cursor-pointer
  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
  disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none
  active:scale-[0.98]
`

export function MwButton({
  children,
  variant = 'primary',
  size = 'md',
  asAnchor = false,
  href,
  className = '',
  ...rest
}: MwButtonProps) {
  const [hovered, setHovered] = useState(false)
  const v = VARIANT_STYLES[variant]

  const style: CSSProperties = {
    background: hovered ? v.bgHover : v.bg,
    color: hovered ? v.colorHover : v.color,
    borderColor: hovered ? v.borderHover : v.border,
    transition:
      'transform var(--mw-duration-fast) var(--mw-ease), background-color var(--mw-duration-fast) var(--mw-ease), border-color var(--mw-duration-fast) var(--mw-ease), color var(--mw-duration-fast) var(--mw-ease)',
    fontFamily: 'var(--mw-font-body)',
    height: size === 'lg' ? '52px' : undefined,
    ['--tw-ring-color' as string]: 'var(--mw-accent)',
  }

  const classes = `${BASE} ${SIZE_CLASSES[size]} ${className}`

  if (asAnchor && href) {
    return (
      <a
        href={href}
        className={classes}
        style={style}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {children}
      </a>
    )
  }

  return (
    <button
      className={classes}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...rest}
    >
      {children}
    </button>
  )
}
